import { useMemo } from 'react';
import type { BulleDeFeuille, BulleEnrichie, Feuille } from '../partage/api/types';
import { useFeuilles } from '../partage/useArbre';
import { faitesSurTotal, surN } from '../partage/vocabulaire';
import { arbreVisible, type BulleVisible } from '../hierarchie';
import { attendLeMonde } from './arbreAuPouce';
import { bullesConnues } from './contenuDesMarges';
import { ouvertesDuCouche } from './lignesDeLAnalyseCouchee';
import { placesDeLaHierarchieCouchee } from './placesDeLaHierarchieCouchee';
import './HierarchieCouchee.css';

/**
 * **La Hiérarchie du téléphone couché** (chantier telephone-couche, 03 ; maquette
 * `app-au-telephone.html`, `hierarchie()`, `.hier`, `.hier-in`, `.hn`). L'arbre de la feuille, de
 * gauche à droite, à toutes les profondeurs connues : la racine en galet bleu, les branches
 * (« f/n »), les tâches, reliés par des courbes, bleues, ocre en pointillé vers une branche qui
 * attend le monde ; ce qui attend le monde en ocre, ce qui est fait barré. Il défile.
 *
 * L'arbre et l'ordre sont ceux de la Hiérarchie (`arbreVisible`, l'ordre du serveur de chaque
 * feuille, `ordreHierarchique`) ; tant qu'une branche n'est pas lue, l'ordre de la carte. Les
 * places : `placesDeLaHierarchieCouchee.ts`. Une branche touchée y entre (son détail, couché) ;
 * une tâche touchée ouvre sa fiche.
 */
export function HierarchieCouchee({
  feuille,
  onEntrer,
  onFiche,
}: {
  feuille: Feuille;
  onEntrer: (id: string) => void;
  onFiche: (id: string) => void;
}) {
  const ouvertes = useMemo(() => ouvertesDuCouche(feuille), [feuille]);
  const lues = useFeuilles(ouvertes);
  const feuilleDe = new Map(ouvertes.map((id, i) => [id, lues[i]?.data]));

  const connues = bullesConnues(feuille);
  const contenuDe = (id: string) => {
    if (id === feuille.bulle.id) return { enfants: feuille.enfants as BulleEnrichie[], ordre: feuille.ordreHierarchique };
    const sienne = feuilleDe.get(id);
    if (sienne) return { enfants: sienne.enfants as BulleEnrichie[], ordre: sienne.ordreHierarchique };
    const dedans = connues.filter((b) => b.parentId === id);
    return { enfants: dedans, ordre: dedans.map((b) => b.id) };
  };
  const { arbre, visibles } = arbreVisible(feuille.bulle.id, contenuDe, new Set([feuille.bulle.id, ...ouvertes]));
  const places = placesDeLaHierarchieCouchee(arbre, (id) => (visibles.get(id)?.bulle.nbEnfants ?? 0) > 0);

  const attend = new Set([...visibles.values()].filter((v) => attendLeMonde(v.bulle)).map((v) => v.bulle.id));
  const { faites, total } = feuille.bulle.decompte;

  return (
    <div className="hier-couchee">
      <div className="hier-couchee-in" style={{ width: places.largeur, height: places.hauteur }}>
        <svg width={places.largeur} height={places.hauteur} aria-hidden="true">
          {places.courbes.map((c) => (
            <path
              key={`${c.de}-${c.a}`}
              d={c.d}
              className={
                c.vers === 'tache'
                  ? 'hier-couchee-courbe hier-couchee-courbe--tache'
                  : `hier-couchee-courbe hier-couchee-courbe--branche${attend.has(c.a) ? ' est-en-attente' : ''}`
              }
            />
          ))}
        </svg>
        {places.galets.map((g) => {
          const style = { left: g.x, top: g.y };
          if (g.sorte === 'racine') {
            return (
              <div key={g.id} className="hier-couchee-hn hier-couchee-hn--racine" style={style}>
                {feuille.bulle.titre}
                <small>{surN(faites, total)}</small>
              </div>
            );
          }
          const vue = visibles.get(g.id);
          if (!vue) return null;
          return <Galet key={g.id} vue={vue} sorte={g.sorte} style={style} onEntrer={onEntrer} onFiche={onFiche} />;
        })}
      </div>
    </div>
  );
}

/** Une branche (`.hn.br`) ou une tâche (`.hn.tc2`), touchée : son détail ou sa fiche. */
function Galet({
  vue,
  sorte,
  style,
  onEntrer,
  onFiche,
}: {
  vue: BulleVisible;
  sorte: 'branche' | 'tache';
  style: { left: number; top: number };
  onEntrer: (id: string) => void;
  onFiche: (id: string) => void;
}) {
  const b = vue.bulle;
  const decompte = (b as Partial<BulleDeFeuille>).decompte;
  const classe = [
    'hier-couchee-hn',
    sorte === 'branche' ? 'hier-couchee-hn--br' : 'hier-couchee-hn--tc',
    attendLeMonde(b) ? 'est-en-attente' : '',
    b.etat === 'fait' ? 'est-fait' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type="button"
      className={classe}
      style={style}
      data-bulle={b.id}
      onClick={() => (sorte === 'branche' ? onEntrer(b.id) : onFiche(b.id))}
    >
      <span className="hier-couchee-t">{b.titre}</span>
      {sorte === 'branche' && decompte && <small>{faitesSurTotal(decompte.faites, decompte.total)}</small>}
    </button>
  );
}
